/**
 * Compact Harbour card view-model. Radar + ladder + contact.
 * Frontend does not invent why-here / plays — only format API fields.
 */

import {
  HARBOUR_UNCLAIMED_OWNER_LABEL,
  isHarbourHandoffColumn,
  isHarbourParkedColumn,
  isHarbourWorkColumn,
} from 'src/constants/harbour'

export function getHarbourCardPlanLabel(card = {}) {
  if (card.paid) {
    const plan = String(card.plan || '').trim()
    if (
      plan &&
      plan.toLowerCase() !== 'paid' &&
      plan.toLowerCase() !== 'free'
    ) {
      return plan
    }
    return 'Paid'
  }
  return 'Free'
}

export function getHarbourCardChefName(card = {}) {
  return String(card.contact?.name || '').trim()
}

/** Raw CS rep name when claimed; null when unclaimed. */
export function getHarbourCardOwnerName(card = {}) {
  if (!isHarbourCardClaimed(card)) {
    return null
  }
  const name = String(card.ownerName || '').trim()
  return name || null
}

/** Card chip — "On: Sam" only once someone has claimed the venue. */
export function getHarbourCardCsOwnerLabel(card = {}) {
  if (!isHarbourCardClaimed(card)) {
    return null
  }
  const name = String(card.ownerName || '').trim()
  if (name) {
    return `On: ${name}`
  }
  if (card.ownerAccountId) {
    return 'On: Claimed'
  }
  return null
}

/** @deprecated Prefer getHarbourCardCsOwnerLabel for card UI. */
export function getHarbourCardOwnerLabel(card = {}) {
  const name = String(card.ownerName || '').trim()
  if (name) {
    return name
  }
  if (card.ownerAccountId) {
    return 'Claimed'
  }
  return HARBOUR_UNCLAIMED_OWNER_LABEL
}

/**
 * Start work is for unclaimed Needs you. Claimed cards (Today / My work)
 * already have an owner — the 409 copy is for someone else, not a self-claim.
 */
export function isHarbourCardClaimed(card = {}) {
  if (card.startWork?.alreadyOwned || card.startWork?.alreadyTaken) {
    return true
  }
  if (String(card.ownerAccountId || '').trim()) {
    return true
  }
  if (String(card.ownerName || '').trim()) {
    return true
  }
  return Boolean(
    isHarbourWorkColumn(card.column) || isHarbourParkedColumn(card.column),
  )
}

export function canHarbourStartWork(card = {}) {
  const parked = isHarbourParkedColumn(card.column)
  if (
    card.startWork &&
    Object.prototype.hasOwnProperty.call(card.startWork, 'canStartWork')
  ) {
    return (
      Boolean(card.startWork.canStartWork) &&
      !card.startWork.alreadyTaken &&
      (!card.startWork.alreadyOwned || parked)
    )
  }
  return !isHarbourCardClaimed(card)
}

export function shouldShowHarbourStartWork({
  canStartWork,
  alreadyOwned = false,
  alreadyTaken = false,
  column,
} = {}) {
  return canHarbourStartWork({
    column,
    startWork: {
      canStartWork: Boolean(canStartWork),
      alreadyOwned,
      alreadyTaken,
    },
  })
}

export function getHarbourStartWorkSkip(card = {}, viewer = {}) {
  if (canHarbourStartWork(card)) {
    return null
  }
  const ownerId = String(card.ownerAccountId || '')
  const viewerId = String(viewer.accountId || viewer._id || '')
  const ownerName = String(card.ownerName || '').trim()
  const viewerName = String(viewer.name || '').trim()
  const mine = Boolean(
    card.startWork?.alreadyOwned ||
      (ownerId && viewerId && ownerId === viewerId) ||
      (ownerName && viewerName && ownerName === viewerName),
  )
  if (mine) {
    return { alreadyOwned: true }
  }
  return { alreadyTaken: true, ownerName }
}

export function getHarbourCardFollowUpStatus(card = {}) {
  if (card.overdue) {
    return 'overdue'
  }
  if (card.nextFollowUpLabel === 'Today') {
    return 'today'
  }
  if (card.nextFollowUpLabel) {
    return 'upcoming'
  }
  return null
}

export function getHarbourCardAskSalesNudge(card = {}) {
  if (!card.askSalesNudge?.label) {
    return null
  }
  if (isHarbourHandoffColumn(card.column)) {
    return null
  }
  return {
    label: card.askSalesNudge.label,
    upgradeScore:
      card.askSalesNudge.upgradeScore == null
        ? null
        : Number(card.askSalesNudge.upgradeScore),
  }
}

export function getHarbourCardAskProductNudge(card = {}) {
  if (!card.askProductNudge?.label) {
    return null
  }
  if (isHarbourHandoffColumn(card.column)) {
    return null
  }
  return {
    label: card.askProductNudge.label,
    csTouchCount:
      card.askProductNudge.csTouchCount == null
        ? null
        : Number(card.askProductNudge.csTouchCount),
  }
}
